import { useEffect, useRef, useState } from 'react';
import type { CameraErrorKind } from '../types';

export type CameraStatus = 'starting' | 'active' | 'error';

export interface CameraState {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  errorKind: CameraErrorKind | null;
}

/**
 * Owns the getUserMedia lifecycle for the front camera: requests the stream,
 * attaches it to the video element, and stops every track on unmount.
 */
export function useCamera(enabled: boolean): CameraState {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<CameraStatus>('starting');
  const [errorKind, setErrorKind] = useState<CameraErrorKind | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let stream: MediaStream | null = null;

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        if (!cancelled) setStatus('active');
      } catch (error) {
        if (cancelled) return;
        setErrorKind(classifyCameraError(error));
        setStatus('error');
      }
    }

    void start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [enabled]);

  return { videoRef, status, errorKind };
}

function classifyCameraError(error: unknown): CameraErrorKind {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return 'denied';
    if (
      error.name === 'NotFoundError' ||
      error.name === 'OverconstrainedError' ||
      error.name === 'NotReadableError'
    ) {
      return 'unavailable';
    }
  }
  return 'generic';
}
